import React from 'react';
import {
  Dialog, ChoiceCards, RadioList, Button, Field, Select, TextInput, Checkbox, ComboBox, Chip, Banner, Panel,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import { useMemberReportIssue } from './useMemberReportIssue.js';
import { DEFAULT_COL_KEYS, GROUP_BY_OPTIONS } from './memberReportMergeFields.js';
import MemberReportTemplateEditor from './MemberReportTemplateEditor.jsx';
import { formatHebrewDateTime } from '../../../utils/hebrewDate';
import { FormGrid, Row } from '../../../next/ui';

/**
 * הנפקת דוח חברים — new design. Same state and actions (useMemberReportIssue) as the legacy dialog:
 * report type picker → population, letter template (+ design editor), breakdown key, columns, manual columns,
 * related data with period / dates, history, and print / Excel / ZIP per kolel.
 */
const KOLEL_SLOT_HINT = {
  primary: 'קיבוץ לפי כולל ראשי (כמו היום).',
  morning: 'כל חבר ישויך לקובץ לפי כולל לפנה"צ שלו.',
  afternoon: 'כל חבר ישויך לקובץ לפי כולל אחה"צ שלו.',
  both: 'חבר עם שני סלוטים יופיע בשני קבצים (בוקר ואחה"צ).',
};

export default function MemberReportIssueDialogNext(props) {
  const r = useMemberReportIssue(props);
  const {
    open, onClose, step, setStep, reportType, source, setSource, selectedCols, setSelectedCols, includeRegistrations, setIncludeRegistrations,
    includeGrades, setIncludeGrades, contentMode, setContentMode, kolelSlot, setKolelSlot, groupBy, setGroupBy, includeMembers, setIncludeMembers,
    examPeriod, setExamPeriod, dateFrom, setDateFrom, dateTo, setDateTo, templateId, setTemplateId, editorOpen, setEditorOpen, history, showHistory,
    customColumns, customColumnPlacement, setCustomColumnPlacement, customColumnInput, setCustomColumnInput, busy, error, setError,
    isByKolel, needsRelatedFilters, showCustomColumns, columnOptions, typeTemplates, pickerOptions, loadTemplates, loadHistory, pickType, toggleCol,
    addCustomColumn, removeCustomColumn, handlePrint, handleExcel, handleByKolelZip, typeTitle, filteredCount, selectedIds, periods,
  } = r;

  const footer = (
    <>
      {step === 'options' ? <Button icon="right" disabled={busy} onClick={() => setStep('picker')}>חזרה</Button> : null}
      <span className="ck-grow" />
      <Button variant="ghost" disabled={busy} onClick={onClose}>ביטול</Button>
      {step === 'options' && !isByKolel ? (
        <>
          <Button icon="download" disabled={busy} onClick={handleExcel}>{busy ? 'מכין…' : 'ייצוא Excel'}</Button>
          <Button variant="primary" icon="file" disabled={busy} onClick={handlePrint}>{busy ? 'מכין…' : 'הדפס / PDF'}</Button>
        </>
      ) : null}
      {step === 'options' && isByKolel ? (
        <>
          <Button icon="folder" disabled={busy} onClick={() => handleByKolelZip('xlsx-zip')}>{busy ? 'מפיק…' : 'ייצוא Excel (ZIP)'}</Button>
          <Button variant="primary" icon="file" disabled={busy} onClick={() => handleByKolelZip('pdf-zip')}>{busy ? 'מפיק…' : 'ייצוא PDF (ZIP)'}</Button>
        </>
      ) : null}
    </>
  );

  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : onClose} title={step === 'picker' ? 'הנפקת דוח' : typeTitle} icon="file" size="lg" footer={footer}>
        <div className="nx-stack">
          {error ? <Banner tone="danger" icon="alert" onClose={() => setError('')}>{error}</Banner> : null}

          {step === 'picker' ? (
            <>
              <p className="nx-desc">בחרו סוג דוח. עיצוב המכתב ורקע התמונה מוגדרים ב״התאם עיצוב ורקע״ בשלב הבא.</p>
              <ChoiceCards
                value={null}
                onChange={pickType}
                columns={2}
                options={pickerOptions.map((o) => ({ value: o.key, label: o.title, description: o.description, icon: o.ckIcon }))}
              />
            </>
          ) : (
            <>
              <Field label="אוכלוסייה">
                <RadioList
                  value={source}
                  onChange={setSource}
                  options={[
                    { value: 'filters', label: `לפי הסינון בטבלה (${Number(filteredCount || 0).toLocaleString('he-IL')})` },
                    { value: 'selected', label: `לפי השורות שנבחרו (${selectedIds.length.toLocaleString('he-IL')})`, disabled: !selectedIds.length },
                    { value: 'all', label: 'כל החברים' },
                  ]}
                />
              </Field>

              {isByKolel ? (
                <FormGrid cols={2}>
                  <Field label="מפתח לפיצול" hint={KOLEL_SLOT_HINT[kolelSlot]}>
                    <RadioList value={kolelSlot} onChange={setKolelSlot} options={[{ value: 'primary', label: 'כולל ראשי' }, { value: 'morning', label: 'לפנה"צ' }, { value: 'afternoon', label: 'אחה"צ' }, { value: 'both', label: 'גם וגם — קבצים נפרדים לפנה"צ ולאחה"צ' }]} />
                  </Field>
                  <Field label="תוכן בכל קובץ" hint="יופק קובץ Excel נפרד לכל כולל, באריזת ZIP מוכנה לשליחה לגבאים. כל קובץ יכלול טבלת חברים מלאה עם העמודות שנבחרו.">
                    <RadioList value={contentMode} onChange={setContentMode} options={[{ value: 'members', label: 'רשימת חברים' }, { value: 'registrations', label: 'רשימת הרשמות' }, { value: 'both', label: 'שניהם (חברים + הרשמות)' }]} />
                  </Field>
                </FormGrid>
              ) : (
                <Panel
                  title="עיצוב מכתב ורקע"
                  sub="בחרו תבנית טקסט. להתאמת תמונת הרקע והמרווחים — לחצו ״התאם עיצוב ורקע״."
                  icon="edit"
                  actions={<Button size="sm" icon="edit" onClick={() => setEditorOpen(true)}>התאם עיצוב ורקע</Button>}
                >
                  <div className="nx-stack">
                    <Field label="תבנית">
                      <ComboBox
                        value={typeTemplates.find((t) => String(t._id) === String(templateId)) || null}
                        onChange={(v) => setTemplateId(v ? String(v._id) : '')}
                        options={typeTemplates}
                        getLabel={(o) => o?.name || ''}
                        getKey={(o) => o?._id}
                        placeholder="ללא / ברירת מחדל"
                        clearable
                      />
                    </Field>
                    {reportType === 'list' || reportType === 'breakdown' ? <Banner tone="info" icon="info">בהדפסה: עמוד המכתב יוצא עם רקע המכתב; טבלת הנתונים בעמוד הבא.</Banner> : null}
                    {reportType === 'card' ? <Banner tone="info" icon="info">כל כרטיס נבחן יוצא על עמוד A4 עם אותה תבנית ורקע מכתב.</Banner> : null}
                  </div>
                </Panel>
              )}

              {reportType === 'breakdown' ? (
                <FormGrid cols={2}>
                  <Field label="פילוח לפי"><Select value={groupBy} onChange={(v) => setGroupBy(v || 'city')} options={GROUP_BY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))} /></Field>
                  <div style={{ alignSelf: 'end' }}><Checkbox checked={includeMembers} onChange={setIncludeMembers} label="כלול שמות חברים בכל קבוצה" /></div>
                </FormGrid>
              ) : null}

              {contentMode !== 'registrations' || !isByKolel ? (
                <Field label="עמודות לייצוא">
                  <div className="nx-stack" style={{ gap: 6 }}>
                    <Row>
                      <Button size="sm" variant="ghost" onClick={() => setSelectedCols(new Set(columnOptions.map((c) => c.key)))}>הכל</Button>
                      <Button size="sm" variant="ghost" onClick={() => setSelectedCols(new Set(DEFAULT_COL_KEYS))}>ברירת מחדל</Button>
                      <small className="ck-muted">{`נבחרו ${selectedCols.size}`}</small>
                    </Row>
                    <div style={{ maxHeight: 170, overflow: 'auto' }}>
                      <Row gap={6}>
                        {columnOptions.map((c) => (
                          <Chip key={c.key} tone={selectedCols.has(c.key) ? 'primary' : 'neutral'} icon={selectedCols.has(c.key) ? 'check' : undefined} onClick={() => toggleCol(c.key)}>{c.label}</Chip>
                        ))}
                      </Row>
                    </div>
                  </div>
                </Field>
              ) : null}

              {showCustomColumns ? (
                <Field label="עמודות נוספות למילוי ידני" hint="יופיעו בטבלה כעמודות ריקות — למילוי גביה, הערות וכדומה">
                  <div className="nx-stack" style={{ gap: 8 }}>
                    <Row>
                      <div style={{ flex: 1 }}>
                        <TextInput
                          value={customColumnInput}
                          placeholder="שם עמודה"
                          maxLength={40}
                          disabled={customColumns.length >= 10}
                          onChange={(e) => setCustomColumnInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomColumn(); } }}
                        />
                      </div>
                      <Button icon="plus" disabled={!customColumnInput.trim() || customColumns.length >= 10} onClick={addCustomColumn}>הוסף</Button>
                    </Row>
                    {customColumns.length ? (
                      <>
                        <Row>{customColumns.map((label) => <Chip key={label} onRemove={() => removeCustomColumn(label)}>{label}</Chip>)}</Row>
                        <Field label="מיקום העמודות הנוספות">
                          <RadioList inline value={customColumnPlacement} onChange={setCustomColumnPlacement} options={[{ value: 'start', label: 'בתחילת הטבלה' }, { value: 'end', label: 'בסוף הטבלה' }]} />
                        </Field>
                      </>
                    ) : null}
                  </div>
                </Field>
              ) : null}

              <hr className="nx-hr" />

              {!isByKolel ? (
                <Field label="נתונים קשורים">
                  <Row>
                    <Checkbox checked={includeRegistrations} onChange={setIncludeRegistrations} label="רישומים לבחינות" />
                    <Checkbox checked={includeGrades} onChange={setIncludeGrades} label="ציונים" />
                  </Row>
                </Field>
              ) : null}

              {needsRelatedFilters ? (
                <FormGrid cols={3}>
                  <Field label="מועד (אופציונלי)">
                    <ComboBox value={examPeriod} onChange={setExamPeriod} options={periods} getLabel={(o) => o?.name || ''} getKey={(o) => o?._id} placeholder="כל המועדים" clearable />
                  </Field>
                  <Field label="מתאריך"><TextInput type="date" ltr value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></Field>
                  <Field label="עד תאריך"><TextInput type="date" ltr value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></Field>
                </FormGrid>
              ) : null}

              {isByKolel && busy ? <Banner tone="info" icon="clock">מפיק קבצים לפי כולל — ייצוא PDF לכ־80 כוללים עשוי להימשך דקה או יותר…</Banner> : null}

              <div className="nx-stack" style={{ gap: 6 }}>
                <div><Button size="sm" variant="ghost" icon="history" onClick={loadHistory}>היסטוריית הנפקות</Button></div>
                {showHistory ? (
                  <div style={{ maxHeight: 140, overflow: 'auto' }}>
                    {!history.length ? <small className="ck-muted">אין הנפקות קודמות</small> : null}
                    {history.slice(0, 10).map((h) => (
                      <div key={h._id}><small className="ck-muted">{`${formatHebrewDateTime(h.issuedAt)} · ${h.reportType} · ${h.outputFormat} · ${h.memberCount} חברים · ${h.issuedBy}`}</small></div>
                    ))}
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </Dialog>

      <MemberReportTemplateEditor
        open={editorOpen}
        onClose={() => { setEditorOpen(false); loadTemplates(reportType); }}
        initialTargetType={reportType === 'byKolel' ? 'list' : reportType}
      />
    </>
  );
}
